import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Users, Calendar, DollarSign, TrendingUp, 
  CheckCircle, Clock, BadgeCheck, XCircle, FileText,
  Eye, AlertTriangle, CreditCard, Wallet, PiggyBank,
  Settings, Lock, Mail, User, Save, Truck, Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import DashboardLayout from "../components/DashboardLayout";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const AdminDashboard = () => {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showAppDialog, setShowAppDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  
  // Delete confirmation state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
  const [clearDataDialog, setClearDataDialog] = useState(false);
  
  // Settings state
  const [profileForm, setProfileForm] = useState({
    name: "",
    phone: ""
  });
  const [emailForm, setEmailForm] = useState({
    new_email: "",
    password: ""
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || "",
        phone: user.phone || ""
      });
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, vendorsRes, bookingsRes, appsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/vendors`, { withCredentials: true }),
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/vendor-applications`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setVendors(vendorsRes.data);
      setBookings(bookingsRes.data);
      setApplications(appsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyVendor = async (vendorId) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/verify`, {}, { withCredentials: true });
      toast.success("Vendor verified!");
      fetchData();
    } catch (error) {
      console.error("Error verifying vendor:", error);
      toast.error("Failed to verify vendor");
    }
  };

  // Delete functions
  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("User deleted successfully");
      setDeleteDialog({ open: false, type: null, item: null });
      fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    try {
      await axios.delete(`${API}/admin/vendors/${vendorId}`, { withCredentials: true });
      toast.success("Vendor deleted successfully");
      setDeleteDialog({ open: false, type: null, item: null });
      fetchData();
    } catch (error) {
      console.error("Error deleting vendor:", error);
      toast.error(error.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    try {
      await axios.delete(`${API}/admin/vendor-applications/${applicationId}`, { withCredentials: true });
      toast.success("Application deleted successfully");
      setDeleteDialog({ open: false, type: null, item: null });
      fetchData();
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error(error.response?.data?.detail || "Failed to delete application");
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, { withCredentials: true });
      toast.success("Booking deleted successfully");
      setDeleteDialog({ open: false, type: null, item: null });
      fetchData();
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast.error(error.response?.data?.detail || "Failed to delete booking");
    }
  };

  const handleClearAllData = async () => {
    try {
      await axios.post(`${API}/admin/clear-all-data`, {}, { withCredentials: true });
      toast.success("All data cleared successfully");
      setClearDataDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error clearing data:", error);
      toast.error(error.response?.data?.detail || "Failed to clear data");
    }
  };

  const confirmDelete = (type, item) => {
    setDeleteDialog({ open: true, type, item });
  };

  const handleViewApplication = (app) => {
    setSelectedApp(app);
    setAdminNotes("");
    setShowAppDialog(true);
  };

  const handleReviewApplication = async (action) => {
    if (!selectedApp) return;
    
    try {
      await axios.put(
        `${API}/admin/vendor-applications/${selectedApp.application_id}/review?action=${action}${adminNotes ? `&admin_notes=${encodeURIComponent(adminNotes)}` : ""}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Application ${action}d successfully!`);
      setShowAppDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error reviewing application:", error);
      toast.error("Failed to review application");
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, profileForm, { withCredentials: true });
      setUser({ ...user, ...response.data });
      toast.success("Profile updated!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await axios.put(`${API}/auth/change-email`, emailForm, { withCredentials: true });
      setUser({ ...user, email: emailForm.new_email });
      setEmailForm({ new_email: "", password: "" });
      toast.success("Email changed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change email");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { withCredentials: true });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password changed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const pendingApplications = applications.filter(a => a.status === "pending" || a.status === "under_review");

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Admin Dashboard
          </h1>
          <p className="text-white/60">Manage users, vendors, revenue, and settings</p>
        </div>

        {/* Stats Cards - Row 1: Users & Vendors */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {[
            { label: "Total Users", value: stats?.total_users || 0, icon: Users, color: "text-blue-400" },
            { label: "Customers", value: stats?.total_customers || 0, icon: User, color: "text-cyan-400" },
            { label: "Vendors", value: stats?.total_vendors || 0, icon: BadgeCheck, color: "text-purple-400" },
            { label: "Pending Apps", value: stats?.pending_applications || 0, icon: FileText, color: "text-orange-400" },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-charcoal rounded-xl border border-white/5 p-5"
            >
              <div className="flex items-center justify-between mb-2">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="font-heading text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-white/40 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Stats Cards - Row 2: Financial */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Revenue", value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: "text-gold", bg: "bg-gold/10" },
            { label: "Outstanding", value: `$${(stats?.outstanding_balance || 0).toLocaleString()}`, icon: Wallet, color: "text-red-400", bg: "bg-red-500/10" },
            { label: "Paid Bookings", value: stats?.paid_bookings || 0, icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/10" },
            { label: "Unpaid Bookings", value: stats?.unpaid_bookings || 0, icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/10" },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 + 0.2 }}
              className={`rounded-xl border border-white/5 p-5 ${stat.bg || 'bg-charcoal'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className={`font-heading text-2xl font-bold ${stat.color === 'text-gold' ? 'text-gold' : 'text-white'}`}>{stat.value}</p>
              <p className="text-white/40 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Choice Mobile Stats */}
        <div className="bg-charcoal rounded-xl border border-white/5 p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Truck className="w-5 h-5 text-gold" />
            <h3 className="font-heading text-lg font-bold text-white">Choice Mobile Celebrations</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-white/40 text-sm">Total Bookings</p>
              <p className="font-heading text-xl font-bold text-white">{stats?.cmc_bookings || 0}</p>
            </div>
            <div>
              <p className="text-white/40 text-sm">Paid Bookings</p>
              <p className="font-heading text-xl font-bold text-green-400">{stats?.cmc_paid || 0}</p>
            </div>
            <div>
              <p className="text-white/40 text-sm">Revenue</p>
              <p className="font-heading text-xl font-bold text-gold">${(stats?.cmc_revenue || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="applications" className="space-y-6">
          <TabsList className="bg-charcoal border border-white/5 rounded-full p-1">
            <TabsTrigger 
              value="applications" 
              className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              Applications
              {pendingApplications.length > 0 && (
                <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
                  {pendingApplications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="vendors" 
              className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              Vendors
            </TabsTrigger>
            <TabsTrigger 
              value="users" 
              className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              Users
            </TabsTrigger>
            <TabsTrigger 
              value="bookings" 
              className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="applications">
            <div className="space-y-4">
              {pendingApplications.length === 0 ? (
                <div className="text-center py-12 bg-charcoal rounded-xl border border-white/5">
                  <CheckCircle className="w-12 h-12 text-green-500/30 mx-auto mb-4" />
                  <p className="text-white/60">No pending applications</p>
                </div>
              ) : (
                pendingApplications.map((app) => (
                  <div 
                    key={app.application_id}
                    className="bg-charcoal rounded-xl border border-white/5 p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h3 className="font-heading text-lg font-bold text-white mb-1">
                          {app.business_name}
                        </h3>
                        <p className="text-white/60 text-sm mb-2">
                          {app.user?.email} • {app.category}
                        </p>
                        <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                          {app.status}
                        </Badge>
                      </div>
                      <Button
                        onClick={() => handleViewApplication(app)}
                        className="bg-gold text-black hover:bg-gold-light rounded-full"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Review
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="vendors">
            <div className="space-y-4">
              {vendors.length === 0 ? (
                <div className="text-center py-12 bg-charcoal rounded-xl border border-white/5">
                  <BadgeCheck className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                  <p className="text-white/60">No vendors yet</p>
                </div>
              ) : (
                vendors.map((vendor) => (
                  <div 
                    key={vendor.vendor_id}
                    className="bg-charcoal rounded-xl border border-white/5 p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading text-lg font-bold text-white">
                            {vendor.business_name}
                          </h3>
                          {vendor.is_verified && (
                            <BadgeCheck className="w-5 h-5 text-gold" />
                          )}
                        </div>
                        <p className="text-white/60 text-sm">
                          {vendor.category} • ${vendor.hourly_rate}/hr
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!vendor.is_verified && (
                          <Button
                            onClick={() => handleVerifyVendor(vendor.vendor_id)}
                            variant="outline"
                            className="border-gold text-gold hover:bg-gold hover:text-black rounded-full"
                          >
                            Verify Vendor
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDelete('vendor', vendor)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-vendor-${vendor.vendor_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="bg-charcoal rounded-xl border border-white/5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left p-4 text-white/60 font-medium">Name</th>
                      <th className="text-left p-4 text-white/60 font-medium">Email</th>
                      <th className="text-left p-4 text-white/60 font-medium">Role</th>
                      <th className="text-left p-4 text-white/60 font-medium">Joined</th>
                      <th className="text-right p-4 text-white/60 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-b border-white/5">
                        <td className="p-4 text-white">{u.name}</td>
                        <td className="p-4 text-white/60">{u.email}</td>
                        <td className="p-4">
                          <Badge 
                            variant="outline"
                            className={
                              u.role === 'admin' 
                                ? 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                                : u.role === 'vendor'
                                ? 'bg-gold/10 text-gold border-gold/20'
                                : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                            }
                          >
                            {u.role}
                          </Badge>
                        </td>
                        <td className="p-4 text-white/40 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-right">
                          {u.role !== 'admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => confirmDelete('user', u)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <div className="space-y-4">
              {bookings.length === 0 ? (
                <div className="text-center py-12 bg-charcoal rounded-xl border border-white/5">
                  <Calendar className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                  <p className="text-white/60">No bookings yet</p>
                </div>
              ) : (
                bookings.map((booking) => (
                  <div 
                    key={booking.booking_id}
                    className="bg-charcoal rounded-xl border border-white/5 p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h3 className="font-heading text-lg font-bold text-white mb-1">
                          {booking.event_type}
                        </h3>
                        <p className="text-white/60 text-sm">
                          {new Date(booking.event_date).toLocaleDateString()} • ${booking.total_amount}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline"
                          className={
                            booking.status === 'confirmed' 
                              ? 'bg-green-500/10 text-green-400 border-green-500/20'
                              : booking.status === 'pending'
                              ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                              : 'bg-white/10 text-white/60 border-white/20'
                          }
                        >
                          {booking.status}
                        </Badge>
                        <Badge 
                          variant="outline"
                          className={
                            booking.payment_status === 'paid' 
                              ? 'bg-gold/10 text-gold border-gold/20'
                              : 'bg-red-500/10 text-red-400 border-red-500/20'
                          }
                        >
                          {booking.payment_status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Profile Settings */}
              <div className="bg-charcoal rounded-xl border border-white/5 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <User className="w-5 h-5 text-gold" />
                  <h3 className="font-heading text-lg font-bold text-white">Profile Settings</h3>
                </div>
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-white/60">Name</Label>
                    <Input
                      id="name"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="bg-obsidian border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-white/60">Phone</Label>
                    <Input
                      id="phone"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="bg-obsidian border-white/10 text-white mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={savingProfile}
                    className="bg-gold text-black hover:bg-gold-light rounded-full"
                  >
                    {savingProfile ? "Saving..." : "Save Profile"}
                  </Button>
                </form>
              </div>

              {/* Change Email */}
              <div className="bg-charcoal rounded-xl border border-white/5 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Mail className="w-5 h-5 text-gold" />
                  <h3 className="font-heading text-lg font-bold text-white">Change Email</h3>
                </div>
                <p className="text-white/40 text-sm mb-4">Current: {user?.email}</p>
                <form onSubmit={handleChangeEmail} className="space-y-4">
                  <div>
                    <Label htmlFor="new_email" className="text-white/60">New Email</Label>
                    <Input
                      id="new_email"
                      type="email"
                      value={emailForm.new_email}
                      onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                      required
                      className="bg-obsidian border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_password" className="text-white/60">Current Password</Label>
                    <Input
                      id="email_password"
                      type="password"
                      value={emailForm.password}
                      onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                      required
                      className="bg-obsidian border-white/10 text-white mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={savingEmail}
                    className="bg-gold text-black hover:bg-gold-light rounded-full"
                  >
                    {savingEmail ? "Changing..." : "Change Email"}
                  </Button>
                </form>
              </div>

              {/* Change Password */}
              <div className="bg-charcoal rounded-xl border border-white/5 p-6 lg:col-span-2">
                <div className="flex items-center gap-3 mb-6">
                  <Lock className="w-5 h-5 text-gold" />
                  <h3 className="font-heading text-lg font-bold text-white">Change Password</h3>
                </div>
                <form onSubmit={handleChangePassword} className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="current_password" className="text-white/60">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                      required
                      className="bg-obsidian border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_password" className="text-white/60">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      required
                      className="bg-obsidian border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password" className="text-white/60">Confirm Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      required
                      className="bg-obsidian border-white/10 text-white mt-1"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Button
                      type="submit"
                      disabled={savingPassword}
                      className="bg-gold text-black hover:bg-gold-light rounded-full"
                    >
                      {savingPassword ? "Changing..." : "Change Password"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Application Review Dialog */}
        <Dialog open={showAppDialog} onOpenChange={setShowAppDialog}>
          <DialogContent className="bg-charcoal border-white/10 text-white max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Review Application: {selectedApp?.business_name}
              </DialogTitle>
            </DialogHeader>
            
            {selectedApp && (
              <ScrollArea className="max-h-[60vh] pr-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-white/40 text-sm">Business Name</p>
                      <p className="text-white">{selectedApp.business_name}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-sm">Category</p>
                      <p className="text-white capitalize">{selectedApp.category}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-sm">Email</p>
                      <p className="text-white">{selectedApp.user?.email}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-sm">Phone</p>
                      <p className="text-white">{selectedApp.phone || "N/A"}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-white/40 text-sm">Description</p>
                    <p className="text-white">{selectedApp.description}</p>
                  </div>
                  
                  {selectedApp.license_number && (
                    <div>
                      <p className="text-white/40 text-sm">License Number</p>
                      <p className="text-white">{selectedApp.license_number}</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-white/40 text-sm mb-2">Admin Notes</p>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add notes for this application..."
                      className="bg-obsidian border-white/10 text-white"
                    />
                  </div>
                  
                  <div className="flex gap-4 pt-4">
                    <Button
                      onClick={() => handleReviewApplication("approve")}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleReviewApplication("reject")}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent className="bg-charcoal border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">
                Delete {deleteDialog.type === 'user' ? 'User' : deleteDialog.type === 'vendor' ? 'Vendor' : deleteDialog.type === 'booking' ? 'Booking' : 'Application'}?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">
                {deleteDialog.type === 'user' && (
                  <>Are you sure you want to delete user <span className="text-white font-medium">{deleteDialog.item?.name}</span>? This will also remove all their associated data including bookings, reviews, and vendor applications.</>
                )}
                {deleteDialog.type === 'vendor' && (
                  <>Are you sure you want to delete vendor <span className="text-white font-medium">{deleteDialog.item?.business_name}</span>? This will also remove all their bookings, contracts, and reviews. The user account will remain.</>
                )}
                {deleteDialog.type === 'booking' && (
                  <>Are you sure you want to delete this booking? This action cannot be undone.</>
                )}
                {deleteDialog.type === 'application' && (
                  <>Are you sure you want to delete this application? This action cannot be undone.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (deleteDialog.type === 'user') {
                    handleDeleteUser(deleteDialog.item?.user_id);
                  } else if (deleteDialog.type === 'vendor') {
                    handleDeleteVendor(deleteDialog.item?.vendor_id);
                  } else if (deleteDialog.type === 'booking') {
                    handleDeleteBooking(deleteDialog.item?.booking_id);
                  } else if (deleteDialog.type === 'application') {
                    handleDeleteApplication(deleteDialog.item?.application_id);
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
